import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  SafeAreaView,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import StyledText from "../../common/ui/styled-text";
import { WalletCards, X } from "lucide-react-native";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  useCheckoutSessionDetails,
  useCreateEmbeddedCheckoutSetupSession,
  useUpdateSubscriptionPayment,
} from "../../../hooks/useBilling";

interface EmbeddedCheckoutModalProps {
  onClose: () => void;
  subscriptionId?: string;
  customerId?: string;
}

const stripePromise: Promise<Stripe | null> | null =
  typeof window !== "undefined"
    ? loadStripe(process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || "")
    : null;

const EmbeddedCheckoutModal: React.FC<EmbeddedCheckoutModalProps> = ({
  onClose,
  subscriptionId,
  customerId,
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { mutateAsync: createSession } =
    useCreateEmbeddedCheckoutSetupSession();
  const { data: sessionDetails, refetch } = useCheckoutSessionDetails(
    sessionId || undefined
  );
  const { mutateAsync: applyPaymentMethod } = useUpdateSubscriptionPayment();

  const fetchClientSecret = useCallback(async () => {
    if (!customerId) throw new Error("Missing customerId for checkout session");
    const res = await createSession({ customerId });
    setSessionId(res.id);
    return res.clientSecret;
  }, [createSession, customerId]);

  // Apply payment method to subscription when session is complete
  useEffect(() => {
    if (!sessionDetails || sessionDetails.status !== "complete") return;
    if (!subscriptionId) {
      onClose();
      return;
    }

    const sid = sessionDetails.setupIntentId;
    if (!sid) {
      throw new Error("Missing setupIntentId for checkout session");
    }

    const payload = { customerId: customerId!, setupIntentId: sid };
    applyPaymentMethod({ subscriptionId, payload }).finally(onClose);
  }, [sessionDetails, subscriptionId, applyPaymentMethod, onClose, customerId]);

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={onClose}
      onDismiss={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <SafeAreaView className="flex-1 bg-black/50">
          <View className="flex-1 items-center justify-center">
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View className="w-full h-full sm:max-w-xl sm:max-h-[80vh] sm:rounded-xl sm:shadow-xl sm:border sm:border-gray-200 bg-white overflow-hidden flex flex-col sm:my-auto sm:mx-auto">
                <View className="items-center p-4 border-b border-gray-200 shadow-sm">
                  <View className="flex flex-row items-center justify-center">
                    <WalletCards
                      className="inline align-middle mr-2"
                      size={20}
                    />
                    <StyledText className="text-xl font-semibold text-center align-middle text-gray-900">
                      Update payment method
                    </StyledText>
                  </View>
                  <TouchableOpacity
                    className="absolute top-3 right-3 cursor-pointer p-2"
                    onPress={onClose}
                  >
                    <X size={20} />
                  </TouchableOpacity>
                </View>
                {/* Scrollable content area for desktop/tablet */}
                <View className="flex-1 overflow-y-auto min-h-0 py-10 bg-[var(--loman-blue)]">
                  <View className="flex-1" id="checkout">
                    <EmbeddedCheckoutProvider
                      stripe={stripePromise}
                      options={{
                        fetchClientSecret,
                        onComplete: () => {
                          // Ensure we have the latest session details
                          refetch();
                        },
                      }}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </View>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </SafeAreaView>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default EmbeddedCheckoutModal;
